/**
 * ClinicalTimeline
 * Horizontal scrollable timeline of a patient's encounter history.
 * Dots are color-coded by encounter type; clicking a dot expands a summary card.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ExternalLink, ChevronRight, ChevronLeft, Activity } from 'lucide-react'
import { api } from '../../lib/api'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Encounter {
  id: string
  encounter_type: string
  procedure_name: string | null
  encounter_date: string
  status: string
  note_count: number
  note_status: string | null
  created_at: string
}

interface ClinicalTimelineProps {
  /** Fetch encounters by patient ID (uses /api/v1/encounters?patient_id=…) */
  patientId?: string
  /** Pre-loaded encounters from parent — skips internal fetch if supplied */
  encounters?: Encounter[]
}

// ─── Encounter-type colour palette ────────────────────────────────────────────

type EncounterCategory = 'surgery' | 'postop' | 'office' | 'lab' | 'er'

const CATEGORY_MAP: Record<string, EncounterCategory> = {
  op_note: 'surgery',
  procedure: 'surgery',
  inpatient: 'postop',
  consult: 'office',
  office: 'office',
  telehealth: 'office',
  lab: 'lab',
  imaging: 'lab',
  er: 'er',
  urgent: 'er',
}

const CATEGORY_CONFIG: Record<
  EncounterCategory,
  { label: string; dot: string; bg: string; border: string; text: string; hex: string }
> = {
  surgery: {
    label: 'Surgery / Procedure',
    hex: '#14b8a6',
    dot: 'bg-[#14b8a6]',
    bg: 'bg-teal-500/10 dark:bg-teal-500/15',
    border: 'border-teal-500/40',
    text: 'text-teal-600 dark:text-teal-400',
  },
  postop: {
    label: 'Post-op / Inpatient',
    hex: '#3b82f6',
    dot: 'bg-[#3b82f6]',
    bg: 'bg-blue-500/10 dark:bg-blue-500/15',
    border: 'border-blue-500/40',
    text: 'text-blue-600 dark:text-blue-400',
  },
  office: {
    label: 'Office Visit',
    hex: '#6b7280',
    dot: 'bg-[#6b7280]',
    bg: 'bg-gray-200/60 dark:bg-gray-700/50',
    border: 'border-gray-400/40 dark:border-gray-600/40',
    text: 'text-gray-600 dark:text-gray-400',
  },
  lab: {
    label: 'Lab / Imaging',
    hex: '#f59e0b',
    dot: 'bg-[#f59e0b]',
    bg: 'bg-amber-500/10 dark:bg-amber-500/15',
    border: 'border-amber-500/40',
    text: 'text-amber-600 dark:text-amber-400',
  },
  er: {
    label: 'ER / Urgent',
    hex: '#ef4444',
    dot: 'bg-[#ef4444]',
    bg: 'bg-red-500/10 dark:bg-red-500/15',
    border: 'border-red-500/40',
    text: 'text-red-600 dark:text-red-400',
  },
}

const ENCOUNTER_TYPE_LABELS: Record<string, string> = {
  op_note: 'Operative Note',
  office: 'Office Visit',
  inpatient: 'Inpatient',
  consult: 'Consultation',
  procedure: 'Procedure',
  telehealth: 'Telehealth',
  lab: 'Lab',
  imaging: 'Imaging',
  er: 'ER Visit',
  urgent: 'Urgent Care',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getCategory(encounterType: string): EncounterCategory {
  return CATEGORY_MAP[encounterType] ?? 'office'
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function isToday(dateStr: string): boolean {
  const d = new Date(dateStr)
  const now = new Date()
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  )
}

// ─── Legend ───────────────────────────────────────────────────────────────────

function Legend() {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
      {(Object.entries(CATEGORY_CONFIG) as [EncounterCategory, (typeof CATEGORY_CONFIG)[EncounterCategory]][]).map(
        ([, cfg]) => (
          <span key={cfg.label} className="flex items-center gap-1.5">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
          </span>
        ),
      )}
    </div>
  )
}

// ─── Tooltip (desktop hover) ──────────────────────────────────────────────────

interface TooltipProps {
  encounter: Encounter
  x: number
  y: number
}

function Tooltip({ encounter, x, y }: TooltipProps) {
  const cat = getCategory(encounter.encounter_type)
  const cfg = CATEGORY_CONFIG[cat]
  return (
    <div
      className="pointer-events-none fixed z-50 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg px-3 py-2 text-xs max-w-[180px]"
      style={{ left: x + 12, top: y - 8 }}
    >
      <p className={`font-semibold ${cfg.text} mb-0.5`}>{ENCOUNTER_TYPE_LABELS[encounter.encounter_type] ?? encounter.encounter_type}</p>
      <p className="text-gray-600 dark:text-gray-300">{formatDate(encounter.encounter_date)}</p>
      {encounter.procedure_name && (
        <p className="text-gray-500 dark:text-gray-400 mt-0.5 truncate">{encounter.procedure_name}</p>
      )}
    </div>
  )
}

// ─── Expanded summary card ────────────────────────────────────────────────────

interface SummaryCardProps {
  encounter: Encounter
  noteExcerpt: string | null
  loadingNote: boolean
}

function SummaryCard({ encounter, noteExcerpt, loadingNote }: SummaryCardProps) {
  const navigate = useNavigate()
  const cat = getCategory(encounter.encounter_type)
  const cfg = CATEGORY_CONFIG[cat]

  function handleNavigate(e: React.MouseEvent) {
    e.stopPropagation()
    if (encounter.note_count > 0) {
      navigate(`/encounter/${encounter.id}/note`)
    } else {
      navigate(`/encounter/${encounter.id}`)
    }
  }

  return (
    <div
      className={`mt-4 rounded-lg border p-4 text-sm ${cfg.bg} ${cfg.border} transition-all`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className={`font-semibold ${cfg.text}`}>
            {ENCOUNTER_TYPE_LABELS[encounter.encounter_type] ?? encounter.encounter_type}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {formatDate(encounter.encounter_date)}
          </p>
        </div>
        <button
          onClick={handleNavigate}
          className="flex items-center gap-1 text-xs text-brand-600 dark:text-brand-400 hover:underline shrink-0 mt-0.5"
        >
          View full encounter
          <ExternalLink size={12} />
        </button>
      </div>

      {/* Procedure name */}
      {encounter.procedure_name && (
        <p className="text-gray-700 dark:text-gray-300 mb-2 font-medium">{encounter.procedure_name}</p>
      )}

      {/* Note excerpt */}
      {loadingNote ? (
        <p className="text-gray-400 dark:text-gray-500 italic text-xs animate-pulse">Loading note…</p>
      ) : noteExcerpt ? (
        <p className="text-gray-600 dark:text-gray-300 text-xs leading-relaxed line-clamp-3">
          {noteExcerpt}
        </p>
      ) : (
        encounter.note_count === 0 && (
          <p className="text-gray-400 dark:text-gray-500 italic text-xs">No note yet</p>
        )
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ClinicalTimeline({ patientId, encounters: propEncounters }: ClinicalTimelineProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  const [encounters, setEncounters] = useState<Encounter[]>(propEncounters ?? [])
  const [loading, setLoading] = useState(!propEncounters)
  const [error, setError] = useState<string | null>(null)

  const [activeId, setActiveId] = useState<string | null>(null)
  const [noteExcerpts, setNoteExcerpts] = useState<Record<string, string | null>>({})
  const [loadingNote, setLoadingNote] = useState(false)

  const [tooltip, setTooltip] = useState<{ encounter: Encounter; x: number; y: number } | null>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // ── Fetch if not pre-loaded ──
  useEffect(() => {
    if (propEncounters) {
      setEncounters(propEncounters)
      return
    }
    if (!patientId) return
    ;(async () => {
      try {
        setLoading(true)
        // Prefer the specific patient-encounters endpoint; fall back to query param style
        const data = await api.getPatientEncounters(patientId)
        // Sort oldest → newest
        const sorted = [...data].sort(
          (a, b) => new Date(a.encounter_date).getTime() - new Date(b.encounter_date).getTime(),
        )
        setEncounters(sorted)
      } catch (err: any) {
        setError(err.message || 'Failed to load encounters')
      } finally {
        setLoading(false)
      }
    })()
  }, [patientId, propEncounters])

  // Sort oldest → newest whenever propEncounters updates
  useEffect(() => {
    if (propEncounters) {
      const sorted = [...propEncounters].sort(
        (a, b) => new Date(a.encounter_date).getTime() - new Date(b.encounter_date).getTime(),
      )
      setEncounters(sorted)
    }
  }, [propEncounters])

  // ── Scroll arrows state ──
  function updateScrollState() {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 4)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
  }

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateScrollState()
    el.addEventListener('scroll', updateScrollState, { passive: true })
    const ro = new ResizeObserver(updateScrollState)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', updateScrollState)
      ro.disconnect()
    }
  }, [encounters])

  function scrollBy(px: number) {
    scrollRef.current?.scrollBy({ left: px, behavior: 'smooth' })
  }

  // ── Dot click → expand card + lazy-load note ──
  async function handleDotClick(enc: Encounter) {
    if (activeId === enc.id) {
      setActiveId(null)
      return
    }
    setActiveId(enc.id)
    if (enc.note_count > 0 && !(enc.id in noteExcerpts)) {
      setLoadingNote(true)
      try {
        const notes: any[] = await api.listNotes(enc.id)
        const first = notes?.[0]
        const content: string = first?.content ?? first?.note_text ?? first?.text ?? ''
        setNoteExcerpts((prev) => ({ ...prev, [enc.id]: content.slice(0, 200) || null }))
      } catch {
        setNoteExcerpts((prev) => ({ ...prev, [enc.id]: null }))
      } finally {
        setLoadingNote(false)
      }
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 animate-pulse">
        <div className="h-4 w-36 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
        <div className="flex items-center gap-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="h-2 w-14 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-sm text-red-600 dark:text-red-400">
        Failed to load timeline: {error}
      </div>
    )
  }

  if (encounters.length === 0) {
    return (
      <div className="rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6 text-center">
        <Activity size={32} className="mx-auto text-gray-400 dark:text-gray-600 mb-2" />
        <p className="text-sm text-gray-500 dark:text-gray-400">No encounter history yet</p>
      </div>
    )
  }

  const activeEncounter = encounters.find((e) => e.id === activeId) ?? null

  return (
    <div className="rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
      {/* Title + legend */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <Activity size={15} className="text-brand-500" />
          Clinical Timeline
          <span className="text-xs font-normal text-gray-400 dark:text-gray-500">
            ({encounters.length} encounter{encounters.length !== 1 ? 's' : ''})
          </span>
        </h3>
        <span className="hidden sm:inline-flex"><Legend /></span>
      </div>

      {/* ── Horizontal scroll track (all sizes) ── */}
      <div>
        <div className="relative">
          {/* Left scroll button */}
          {canScrollLeft && (
            <button
              onClick={() => scrollBy(-240)}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full p-1 shadow hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeft size={16} className="text-gray-500 dark:text-gray-400" />
            </button>
          )}
          {/* Right scroll button */}
          {canScrollRight && (
            <button
              onClick={() => scrollBy(240)}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full p-1 shadow hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              aria-label="Scroll right"
            >
              <ChevronRight size={16} className="text-gray-500 dark:text-gray-400" />
            </button>
          )}

          {/* Scrollable track */}
          <div
            ref={scrollRef}
            className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 pb-2"
            style={{ scrollbarWidth: 'thin' }}
          >
            <div className="relative flex items-center min-w-max px-8 py-4">
              {/* Connecting line */}
              <div className="absolute top-1/2 left-8 right-8 h-px bg-gray-300 dark:bg-gray-700 -translate-y-1/2" />

              {encounters.map((enc, idx) => {
                const cat = getCategory(enc.encounter_type)
                const cfg = CATEGORY_CONFIG[cat]
                const isActive = activeId === enc.id
                const today = isToday(enc.encounter_date)

                return (
                  <div
                    key={enc.id}
                    className="relative flex flex-col items-center mx-3 sm:mx-6 cursor-pointer select-none group"
                    onClick={() => handleDotClick(enc)}
                    onMouseMove={(e) => setTooltip({ encounter: enc, x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setTooltip(null)}
                  >
                    {/* Today indicator */}
                    {today && (
                      <span className="absolute -top-5 text-[10px] font-semibold text-brand-500 dark:text-brand-400 whitespace-nowrap">
                        TODAY
                      </span>
                    )}

                    {/* Dot */}
                    <div
                      className={`
                        w-4 h-4 rounded-full z-10 transition-all duration-150
                        ${cfg.dot}
                        ${isActive ? 'ring-2 ring-offset-2 ring-offset-gray-50 dark:ring-offset-gray-900 scale-125' : 'group-hover:scale-110'}
                        ${today ? 'ring-2 ring-brand-400 ring-offset-1 ring-offset-gray-50 dark:ring-offset-gray-900' : ''}
                      `}
                      style={{ boxShadow: isActive ? `0 0 0 3px ${cfg.hex}44` : undefined }}
                    />

                    {/* Date label */}
                    <span className="mt-2 text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                      {new Date(enc.encounter_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
                    </span>

                    {/* Encounter # (small) */}
                    <span className={`text-[9px] font-mono text-gray-300 dark:text-gray-600`}>
                      #{idx + 1}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Expanded card (desktop) */}
        {activeEncounter && (
          <SummaryCard
            encounter={activeEncounter}
            noteExcerpt={noteExcerpts[activeEncounter.id] ?? null}
            loadingNote={loadingNote && activeId === activeEncounter.id}
          />
        )}
      </div>

      {/* Mobile vertical list removed — horizontal timeline works on all sizes */}

      {/* Global tooltip (desktop only) */}
      {tooltip && <Tooltip encounter={tooltip.encounter} x={tooltip.x} y={tooltip.y} />}
    </div>
  )
}

export default ClinicalTimeline
